// src/services/contentService.ts - Updated với Strapi v5 documentId support
import apiClient from '@/api/apiClient';
import axios from 'axios';
import { getDocumentId, updateRecordById, deleteRecordById, bulkUpdateRecords, bulkDeleteRecords, getStrapiErrorMessage } from '@/utils/strapiV5Utils';
import { ID } from '@/types';

// Helper function để xử lý exclude filter an toàn
function addExcludeFilter(queryParams: URLSearchParams, excludeId: string, currentContent?: any) {
    // Kiểm tra xem excludeId có phải là integer không
    const isIntegerId = /^\d+$/.test(excludeId);

    if (isIntegerId) {
        // Dùng id filter cho integer
        queryParams.append('filters[id][$ne]', excludeId);
    } else {
        // Dùng documentId filter cho string (Strapi v5)
        queryParams.append('filters[documentId][$ne]', excludeId);
    }

    // Nếu có currentContent với documentId khác, exclude nó luôn  
    if (currentContent?.documentId && currentContent.documentId !== excludeId) {
        queryParams.append('filters[documentId][$ne]', currentContent.documentId);
    }
}
/**
 * Service xử lý thao tác với nội dung bản quyền - Compatible với Strapi v5
 */
class ContentService {
    /**
     * Lấy danh sách danh mục nội dung
     */
    async getCategories() {
        try {
            // Gọi API lấy danh sách danh mục
            const response = await apiClient.get('/api/categories', {
                params: {
                    populate: '*',
                    'pagination[limit]': 100 // Lấy tối đa 100 danh mục
                }
            });

            // Kiểm tra và xử lý dữ liệu từ Strapi v5
            if (response.data && response.data.data) {
                return response.data.data; // Trả về dữ liệu danh mục
            }

            return [];
        } catch (error) {
            console.error('Error fetching categories:', error);
            throw error;
        }
    }

    /**
     * Lấy danh sách tags nội dung
     */
    async getTags() {
        try {
            // Gọi API lấy danh sách tags
            const response = await apiClient.get('/api/tags', {
                params: {
                    populate: '*',
                    'pagination[limit]': 100 // Lấy tối đa 100 tags
                }
            });

            // Kiểm tra và xử lý dữ liệu từ Strapi v5
            if (response.data && response.data.data) {
                return response.data.data; // Trả về dữ liệu tags
            }

            return [];
        } catch (error) {
            console.error('Error fetching tags:', error);
            throw error;
        }
    }

    // src/services/contentService.ts - FIXED getAllContents method

    /**
     * Láy danh sách tất cả nội dung với filtering, pagination, sorting
     */
    async getAllContents(options: {
        page?: number;
        limit?: number;
        search?: string;
        category?: string;
        contentKind?: string;
        contentStatus?: string | string[];
        isPublic?: boolean;
        sortBy?: string;
        sortOrder?: 'asc' | 'desc';
        tags?: string[];
        authorId?: string;
        dateFrom?: string;
        dateTo?: string;
        populate?: string[];
    } = {}) {
        try {
            const {
                page = 1,
                limit = 12,
                search,
                category,
                contentKind,
                contentStatus,
                isPublic,
                sortBy = 'createdAt',
                sortOrder = 'desc',
                tags,
                authorId,
                dateFrom,
                dateTo,
                populate = [
                    'user.user_profile.avatar',
                    'generatedBy.user_profile.avatar',
                    'owners.user_profile.avatar',
                    'category',
                    'tags',
                    'files',
                    'thumbnail'
                ]
            } = options;

            console.log('🔍 ContentService: Fetching contents with options:', options);

            // Build basic params
            const params: any = {
                'pagination[page]': page,
                'pagination[pageSize]': limit,
                'sort': `${sortBy}:${sortOrder}`
            };

            // Add populate parameters
            populate.forEach((field, index) => {
                params[`populate[${index}]`] = field;
            });

            // ✅ FIXED: Build filters as separate object first
            const filters: any = {};

            // Search filter - separate from other filters
            if (search && search.trim()) {
                filters.$or = [
                    { title: { $containsi: search.trim() } },
                    { description: { $containsi: search.trim() } }
                ];
            }

            // Content status filter - SEPARATE from $or search
            if (contentStatus) {
                if (Array.isArray(contentStatus)) {
                    filters.contentStatus = { $in: contentStatus };
                } else {
                    filters.contentStatus = { $eq: contentStatus };
                }
            }

            // Content kind filter  
            if (contentKind && contentKind !== 'all') {
                filters.contentKind = { $eq: contentKind };
            }

            // Category filter
            if (category && category !== 'all') {
                filters['category.id'] = { $eq: category };
            }

            // Public/Private filter
            if (isPublic !== undefined) {
                filters.isPublic = { $eq: isPublic };
            }

            // Author filter - add to existing $or if search exists, otherwise create new
            if (authorId) {
                const authorFilters = [
                    { 'user.id': { $eq: authorId } },
                    { 'generatedBy.id': { $eq: authorId } },
                    { 'owners.id': { $eq: authorId } }
                ];

                if (filters.$or) {
                    // If search $or exists, create $and to combine search and author
                    filters.$and = [
                        { $or: filters.$or }, // search conditions
                        { $or: authorFilters } // author conditions
                    ];
                    delete filters.$or; // remove original $or
                } else {
                    // No search, just use author $or
                    filters.$or = authorFilters;
                }
            }

            // Tags filter
            if (tags && tags.length > 0) {
                filters['tags.id'] = { $in: tags };
            }

            // Date range filters
            if (dateFrom || dateTo) {
                filters.createdAt = {};
                if (dateFrom) filters.createdAt.$gte = dateFrom;
                if (dateTo) filters.createdAt.$lte = dateTo;
            }

            // ✅ Convert filters object to URL params
            this.addFiltersToParams(params, filters);

            console.log('🔍 Final API params:', params);

            // Call API
            const response = await apiClient.get('/api/contents', { params });

            const data = response.data.data || [];
            const pagination = response.data.meta?.pagination || {
                page,
                pageSize: limit,
                pageCount: 1,
                total: 0
            };

            console.log(`✅ ContentService: Found ${data.length} contents (total: ${pagination.total})`);

            return {
                data,
                pagination,
                meta: {
                    ...response.data.meta,
                    searchQuery: search,
                    filters: options
                }
            };

        } catch (error: any) {
            console.error('❌ ContentService: getAllContents failed:', error);

            if (error.response) {
                console.error('API Error Response:', error.response.data);
                console.error('API Error Status:', error.response.status);
            }

            return {
                data: [],
                pagination: {
                    page: 1,
                    pageSize: limit || 12,
                    pageCount: 1,
                    total: 0
                },
                meta: {
                    error: error.message,
                    filters: options
                }
            };
        }
    }

    /**
     * ✅ NEW: Helper method to convert filters object to URL params
     */
    private addFiltersToParams(params: any, filters: any, prefix = 'filters') {
        Object.keys(filters).forEach(key => {
            const value = filters[key];

            if (key === '$or' && Array.isArray(value)) {
                // Handle $or arrays
                value.forEach((condition, index) => {
                    Object.keys(condition).forEach(field => {
                        const operation = condition[field];
                        if (typeof operation === 'object' && operation !== null) {
                            Object.keys(operation).forEach(op => {
                                params[`${prefix}[$or][${index}][${field}][${op}]`] = operation[op];
                            });
                        } else {
                            params[`${prefix}[$or][${index}][${field}]`] = operation;
                        }
                    });
                });
            } else if (key === '$and' && Array.isArray(value)) {
                // Handle $and arrays
                value.forEach((condition, index) => {
                    this.addFiltersToParams(params, condition, `${prefix}[$and][${index}]`);
                });
            } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                // Handle nested objects like { $eq: 'value' }
                Object.keys(value).forEach(operation => {
                    params[`${prefix}[${key}][${operation}]`] = value[operation];
                });
            } else {
                // Handle simple values
                params[`${prefix}[${key}]`] = value;
            }
        });
    }

    /**
     * ✅ BONUS: Lấy nội dung featured/nổi bật
     */
    async getFeaturedContents(limit: number = 6) {
        try {
            console.log(`🌟 Fetching ${limit} featured contents`);

            return this.getAllContents({
                limit,
                isPublic: true,
                contentStatus: 'approved',
                sortBy: 'registrationDate',
                sortOrder: 'desc',
                // Có thể thêm filter cho isFeatured nếu có field này
            });
        } catch (error) {
            console.error('Error fetching featured contents:', error);
            throw error;
        }
    }

    /**
     * ✅ BONUS: Lấy nội dung mới nhất
     */
    async getLatestContents(limit: number = 10) {
        try {
            console.log(`📅 Fetching ${limit} latest contents`);

            return this.getAllContents({
                limit,
                isPublic: true,
                contentStatus: 'approved',
                sortBy: 'createdAt',
                sortOrder: 'desc'
            });
        } catch (error) {
            console.error('Error fetching latest contents:', error);
            throw error;
        }
    }

    /**
     * ✅ BONUS: Lấy nội dung theo category cụ thể
     */
    async getContentsByCategory(categoryId: string, options: {
        page?: number;
        limit?: number;
        sortBy?: string;
        sortOrder?: 'asc' | 'desc';
    } = {}) {
        try {
            console.log(`📂 Fetching contents for category: ${categoryId}`);

            return this.getAllContents({
                ...options,
                category: categoryId,
                isPublic: true,
                contentStatus: 'approved'
            });
        } catch (error) {
            console.error('Error fetching contents by category:', error);
            throw error;
        }
    }

    /**
     * ✅ BONUS: Search contents with advanced options
     */
    async searchContents(searchQuery: string, options: {
        page?: number;
        limit?: number;
        category?: string;
        contentKind?: string;
    } = {}) {
        try {
            console.log(`🔍 Searching contents for: "${searchQuery}"`);

            if (!searchQuery.trim()) {
                throw new Error('Search query is required');
            }

            return this.getAllContents({
                ...options,
                search: searchQuery.trim(),
                isPublic: true,
                contentStatus: 'approved'
            });
        } catch (error) {
            console.error('Error searching contents:', error);
            throw error;
        }
    }

    /**
     * ✅ BONUS: Get content statistics
     */
    // async getContentStatistics() {
    //     try {
    //         console.log('📊 Fetching content statistics');

    //         // Lấy một số thống kê cơ bản
    //         const [
    //             totalPublic,
    //             totalPrivate,
    //             totalDraft,
    //             totalPending,
    //             totalApproved,
    //             byTypeStats
    //         ] = await Promise.all([
    //             // Total public contents
    //             this.getAllContents({ limit: 1, isPublic: true, contentStatus: 'approved' }),

    //             // Total private contents  
    //             this.getAllContents({ limit: 1, isPublic: false, contentStatus: 'approved' }),

    //             // Draft contents
    //             this.getAllContents({ limit: 1, contentStatus: 'draft' }),

    //             // Pending contents
    //             this.getAllContents({ limit: 1, contentStatus: 'pending' }),

    //             // Approved contents
    //             this.getAllContents({ limit: 1, contentStatus: 'approved' }),

    //             // Stats by content type
    //             this.getContentStatsByType()
    //         ]);

    //         const stats = {
    //             total: {
    //                 public: totalPublic.pagination.total,
    //                 private: totalPrivate.pagination.total,
    //                 draft: totalDraft.pagination.total,
    //                 pending: totalPending.pagination.total,
    //                 approved: totalApproved.pagination.total,
    //             },
    //             byType: byTypeStats,
    //             lastUpdated: new Date().toISOString()
    //         };

    //         console.log('📊 Content statistics:', stats);
    //         return stats;
    //     } catch (error) {
    //         console.error('Error fetching content statistics:', error);
    //         throw error;
    //     }
    // }

    /**
     * ✅ HELPER: Get statistics by content type
     */
    private async getContentStatsByType() {
        const contentTypes = ['image', 'text', 'video', 'audio', 'other'];
        const typeStats: Record<string, number> = {};

        for (const type of contentTypes) {
            try {
                const result = await this.getAllContents({
                    limit: 1,
                    contentKind: type,
                    isPublic: true,
                    contentStatus: 'approved'
                });
                typeStats[type] = result.pagination.total;
            } catch (error) {
                console.error(`Error getting stats for type ${type}:`, error);
                typeStats[type] = 0;
            }
        }

        return typeStats;
    }

    /**
     * ✅ FIXED: Lấy danh sách nội dung của người dùng hiện tại
     */
    async getUserContents(page: number = 1, limit: number = 12, filter?: any, userId?: ID, ) {
        try {
            // ✅ FIX 1: Lấy user ID từ token hoặc từ /api/users/me
            let currentUserId = null;

            if (!userId) {
                try {
                    // Thử lấy thông tin user hiện tại
                    const userResponse = await apiClient.get('/api/users/me');
                    currentUserId = userResponse.data.id;
                    // console.log('Current user ID:', currentUserId);
                } catch (userError) {
                    console.warn('Cannot get current user:', userError);
                    throw new Error('User not authenticated');
                }
            } else {
                currentUserId = userId;
            }

            const { searchTerm, contentKind, status, sortField, sortOrder } = filter;

            const queryParams = new URLSearchParams({
                'pagination[page]': page.toString(),
                'pagination[pageSize]': limit.toString(),
                // 'filters[isPublic][$eq]': 'true',
                'filters[generatedBy][id][$eq]': currentUserId.toString(),
                'sort': `${sortField || 'createdAt'}:${sortOrder || 'desc'}`
            });


            if(!!searchTerm.trim().length) {
                queryParams.append('filters[$or][0][title][$contains]', searchTerm);
                queryParams.append('filters[$or][1][description][$contains]', searchTerm);
            }
            if(contentKind && contentKind !== 'all') {
                queryParams.append('filters[contentKind][$eq]', contentKind);
            }
            if (status && status !== 'all') {
                queryParams.append('filters[contentStatus][$eq]', status);
            }

            const response = await apiClient.get(`/api/contents?${queryParams.toString()}`);
            // console.log('✅ API Response:', response);

            return {
                data: response.data.data || [],
                pagination: response.data.meta?.pagination || {
                    page,
                    pageSize: limit,
                    pageCount: (response?.data?.data?.length || 0 / limit) || 1,
                    total: response.data.data.length || 0
                }
            };
        } catch (error) {
            console.error('Error fetching user contents:', error);

            // ✅ FIX 3: Thử approach khác nếu generatedBy không work
            if (error.response?.status === 400 || error.response?.status === 403) {
                console.log('Trying alternative approach with owners filter...');

                try {
                    const userResponse = await apiClient.get('/api/users/me');
                    const currentUserId = userResponse.data.id;

                    const response = await apiClient.get('/api/contents', {
                        params: {
                            'sort': 'createdAt:desc',
                            'pagination[page]': page,
                            'pagination[pageSize]': limit,
                            // Thử filter theo owners relation
                            'filters[owners][id][$eq]': currentUserId,
                            'populate': '*'
                        }
                    });

                    return {
                        data: response.data.data || [],
                        pagination: response.data.meta?.pagination || {
                            page,
                            pageSize: limit,
                            pageCount: 1,
                            total: 0
                        }
                    };
                } catch (alternativeError) {
                    console.error('Alternative approach also failed:', alternativeError);

                    // ✅ FIX 4: Fallback - lấy tất cả content rồi filter client-side
                    try {
                        const userResponse = await apiClient.get('/api/users/me');
                        const currentUserId = userResponse.data.id;

                        const response = await apiClient.get('/api/contents', {
                            params: {
                                'sort': 'createdAt:desc',
                                'pagination[page]': 1,
                                'pagination[pageSize]': 100, // Lấy nhiều hơn để filter
                                'populate': '*'
                            }
                        });

                        // Filter client-side
                        const userContents = response.data.data.filter(content => {
                            // Check generatedBy
                            if (content.attributes?.generatedBy?.data?.id === currentUserId) {
                                return true;
                            }

                            // Check owners
                            if (content.attributes?.owners?.data?.length > 0) {
                                return content.attributes.owners.data.some(owner => owner.id === currentUserId);
                            }

                            // Check createdBy (nếu có)
                            if (content.attributes?.createdBy?.data?.id === currentUserId) {
                                return true;
                            }

                            return false;
                        });

                        // Apply pagination client-side
                        const startIndex = (page - 1) * limit;
                        const endIndex = startIndex + limit;
                        const paginatedData = userContents.slice(startIndex, endIndex);

                        return {
                            data: paginatedData,
                            pagination: {
                                page,
                                pageSize: limit,
                                pageCount: Math.ceil(userContents.length / limit),
                                total: userContents.length
                            }
                        };
                    } catch (fallbackError) {
                        console.error('All approaches failed:', fallbackError);
                        throw error;
                    }
                }
            }

            throw error;
        }
    }

    /**
     * Đăng ký nội dung mới
     * @param formData FormData chứa thông tin đăng ký và file
     * @param progressCallback Callback để cập nhật tiến trình upload
     */
    async registerContent(formData: FormData, progressCallback?: (progress: number) => void) {
        try {
            // Lấy base URL từ apiClient
            const baseURL = apiClient.client?.defaults.baseURL || import.meta.env.VITE_API_URL || 'http://localhost:1337';

            // Lấy token từ localStorage
            const token = localStorage.getItem('token');

            // Kiểm tra xem formData có trường data chưa
            let dataContent = null;
            try {
                dataContent = formData.get('data');
                if (dataContent) {
                    // Nếu có, parse để xem nội dung
                    console.log("Current data content:", typeof dataContent === 'string' ? JSON.parse(dataContent) : dataContent);
                }
            } catch (e) {
                console.error("Error parsing data content:", e);
            }

            // Tạo formData mới với cấu trúc đúng cho Strapi v5
            const newFormData = new FormData();

            // Lấy thông tin từ formData hiện tại
            const dataStr = formData.get('data') as string;
            if (dataStr) {
                try {
                    const data = JSON.parse(dataStr);

                    // ✅ FIX 5: Thêm user relationship khi tạo content
                    const userResponse = await apiClient.get('/api/users/me');
                    const currentUserId = userResponse.data.id;

                    // Thêm data với đúng cấu trúc cho Strapi v5
                    const strapiData = {
                        title: data.title,
                        description: data.description,
                        contentKind: data.contentType || data.contentKind, // Unify field name
                        category: data.category,
                        isPublic: data.isPublic,
                        contentStatus: data.status || "draft",
                        registrationDate: new Date().toISOString(),
                        tags: Array.isArray(data.tags) ? data.tags : [],
                        // ✅ Ensure user relationships
                        generatedBy: currentUserId,
                        owners: [currentUserId],
                        ownershipDistribution: data.collaborators || {},
                        protectionSettings: data.protection || {},
                    };

                    // Thêm data vào formData mới
                    newFormData.append('data', JSON.stringify(strapiData));

                    // Log để debug
                    console.log("New form data prepared:", strapiData);
                } catch (e) {
                    console.error("Error parsing data:", e);
                    // Nếu parse lỗi, giữ nguyên data
                    newFormData.append('data', dataStr);
                }
            }

            // Thêm các file vào formData mới
            // Trong Strapi v5, fields.files được thay thế bởi files
            const fileEntries = Array.from(formData.entries())
                .filter(([key]) => key.startsWith('files.') || key === 'files');

            for (const [key, value] of fileEntries) {
                if (value instanceof File) {
                    // Chuyển đổi files.content thành files
                    const newKey = key.replace('files.content', 'files');
                    newFormData.append(newKey, value);
                    console.log(`Added file ${value.name} with key ${newKey}`);
                }
            }

            // Log toàn bộ nội dung form mới để debug
            console.log("Form entries:");
            for (const pair of newFormData.entries()) {
                if (pair[1] instanceof File) {
                    console.log(pair[0], pair[1].name);
                } else {
                    console.log(pair[0], pair[1]);
                }
            }

            // Sử dụng axios trực tiếp để có thể theo dõi tiến trình upload
            const response = await axios({
                method: 'POST',
                url: `${baseURL}/api/contents`,
                data: newFormData,
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': token ? `Bearer ${token}` : '',
                },
                onUploadProgress: (progressEvent) => {
                    if (progressCallback && progressEvent.total) {
                        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        progressCallback(percentCompleted);
                    }
                }
            });

            return response.data;
        } catch (error) {
            console.error('Content registration error:', error);

            // Log thêm chi tiết lỗi từ response nếu có
            if (error.response) {
                console.error('Error response data:', error.response.data);
                console.error('Error response status:', error.response.status);
                console.error('Error response headers:', error.response.headers);
            }

            throw error;
        }
    }

    /**
     * ✅ UPDATED: Lấy thông tin chi tiết một nội dung (với documentId support)
     * @param id ID hoặc documentId của nội dung cần lấy
     */
    async getContentById(id: string) {
        try {
            // console.log(`🔍 Fetching content by ID: ${id}`);

            const response = await apiClient.get(`/api/contents/${id}`, {
                params: {
                    populate: '*' // Lấy tất cả thông tin liên quan
                }
            });

            if (response.data && response.data.data) {
                // console.log(`✅ Found content: ${response.data.data.id} (documentId: ${response.data.data.documentId})`);
                return response.data.data;
            }

            throw new Error('Không tìm thấy nội dung');
        } catch (error) {
            console.error('Error fetching content:', error);
            throw error;
        }
    }


    // ✅ FIXED: getRelatedContent function
    async getRelatedContent(contentId: string, options: {
        limit?: number;
        filters?: any;
    } = {}) {
        try {
            const { limit = 6, filters = {} } = options;
            // console.log(`🔗 Fetching related content for: ${contentId}`);

            // Get current content để extract category
            const currentContent = await this.getContentById(contentId);
            if (!currentContent) {
                throw new Error('Content not found');
            }

            const categoryId = currentContent.category?.id ||
                currentContent.category?.documentId ||
                currentContent.attributes?.category?.data?.id;

            const queryParams = new URLSearchParams();

            // ✅ FIXED: Safe exclude filter
            addExcludeFilter(queryParams, contentId, currentContent);

            // ✅ Only approved and public content
            queryParams.append('filters[contentStatus][$eq]', 'approved');
            queryParams.append('filters[isPublic][$eq]', 'true');

            // ✅ Filter by same category if available
            if (categoryId) {
                queryParams.append('filters[category][id][$eq]', categoryId);
            }

            // ✅ Pagination
            queryParams.append('pagination[pageSize]', limit.toString());
            queryParams.append('pagination[page]', '1');

            // ✅ Sort by most relevant
            queryParams.append('sort[0]', 'viewCount:desc');
            queryParams.append('sort[1]', 'createdAt:desc');

            // ✅ Populate all needed relations
            queryParams.append('populate', JSON.stringify([
                'category',
                'owners',
                'files',
                'tags'
            ]));

            const response = await apiClient.get(`/api/contents?${queryParams.toString()}`);

            // console.log(`✅ Found ${response.data.data?.length || 0} related contents`);
            return {
                data: response.data.data || [],
                meta: response.data.meta || {}
            };

        } catch (error) {
            console.error('❌ Error fetching related content:', error);
            return {
                data: [],
                meta: { pagination: { total: 0 } }
            };
        }
    }

    // ✅ FIXED: getContentByUser function  
    async getContentByUser(userId: string, options: {
        limit?: number;
        page?: number;
        filters?: any;
    } = {}) {
        try {
            const { limit = 10, page = 1, filters = {} } = options;
            // console.log(`👤 Fetching content by user: ${userId}`);

            const queryParams = new URLSearchParams();

            // ✅ Filter by user ID trong owners array
            queryParams.append('filters[owners][id][$eq]', userId);

            // ✅ FIXED: Safe additional filters handling
            Object.keys(filters).forEach(key => {
                if (key === 'id' && filters[key].$ne) {
                    // Use safe exclude filter
                    addExcludeFilter(queryParams, filters[key].$ne);
                } else if (filters[key] !== undefined && key !== 'id') {
                    queryParams.append(`filters[${key}]`, String(filters[key]));
                }
            });

            // ✅ Default filters
            if (!filters.contentStatus) {
                queryParams.append('filters[contentStatus][$eq]', 'approved');
            }
            if (!filters.hasOwnProperty('isPublic')) {
                queryParams.append('filters[isPublic][$eq]', 'true');
            }

            // ✅ Pagination
            queryParams.append('pagination[pageSize]', limit.toString());
            queryParams.append('pagination[page]', page.toString());

            // ✅ Sort by newest first
            queryParams.append('sort[0]', 'createdAt:desc');

            // ✅ Populate relations
            queryParams.append('populate', JSON.stringify([
                'category',
                'owners',
                'files',
                'tags'
            ]));

            const response = await apiClient.get(`/api/contents?${queryParams.toString()}`);

            // console.log(`✅ Found ${response.data.data?.length || 0} contents by user ${userId}`);
            return {
                data: response.data.data || [],
                meta: response.data.meta || {}
            };

        } catch (error) {
            console.error('❌ Error fetching content by user:', error);
            return {
                data: [],
                meta: { pagination: { total: 0 } }
            };
        }
    }

    /**
     * ✅ Get all approved public content - for fallback
     */
    
    async getRecentContents() {
        try {
            const queryParams = new URLSearchParams({
                'pagination[page]': '1',
                'pagination[pageSize]': '4',
                'filters[contentStatus][$eq]': 'approved',
                'filters[isPublic][$eq]': 'true',
                'sort[0]': 'viewCount:desc',
                'sort[1]': 'createdAt:desc'
            });

            const response = await apiClient.get(`/api/contents?${queryParams.toString()}`);

            return {
                data: response?.data?.data?.slice(0, 4) || [],
                meta: response.data.meta || {}
            };

        } catch (error) {
            console.error('❌ Error fetching approved content:', error);
            throw error;
        }
    }
    /**
     * ✅ Get all approved public content - for fallback
     */
    async getAllApprovedContent(options: {
        limit?: number;
        excludeId?: string;
    } = {}) {
        try {
            const { limit = 6, excludeId } = options;

            const queryParams = new URLSearchParams();

            // ✅ Only approved and public content
            queryParams.append('filters[contentStatus][$eq]', 'approved');
            queryParams.append('filters[isPublic][$eq]', 'true');

            // ✅ Exclude specific content if provided
            if (excludeId) {
                queryParams.append('filters[id][$ne]', excludeId);
            }

            // ✅ Pagination
            queryParams.append('pagination[pageSize]', limit.toString());
            queryParams.append('pagination[page]', '1');

            // ✅ Sort by most popular
            queryParams.append('sort[0]', 'viewCount:desc');
            queryParams.append('sort[1]', 'createdAt:desc');

            // ✅ Populate relations
            queryParams.append('populate', JSON.stringify([
                'category',
                'owners',
                'files',
                'tags'
            ]));

            const response = await apiClient.get(`/api/contents?${queryParams.toString()}`);

            return {
                data: response.data.data || [],
                meta: response.data.meta || {}
            };

        } catch (error) {
            console.error('❌ Error fetching approved content:', error);
            return {
                data: [],
                meta: { pagination: { total: 0 } }
            };
        }
    }

    /**
     * ✅ NEW: Get content statistics (Real data)
     */
    async getContentStatistics() {
        try {
            // console.log('📊 Fetching content statistics');

            const statsParams = new URLSearchParams();
            statsParams.append('populate', 'category,user');
            statsParams.append('pagination[pageSize]', '1');

            const [
                totalPublic,
                totalPrivate,
                totalDraft,
                totalPending,
                totalApproved
            ] = await Promise.all([
                // Total public contents
                apiClient.get(`/api/contents?filters[isPublic][$eq]=true&filters[contentStatus][$eq]=approved&pagination[pageSize]=1`),

                // Total private contents  
                apiClient.get(`/api/contents?filters[isPublic][$eq]=false&filters[contentStatus][$eq]=approved&pagination[pageSize]=1`),

                // Draft contents
                apiClient.get(`/api/contents?filters[contentStatus][$eq]=draft&pagination[pageSize]=1`),

                // Pending contents
                apiClient.get(`/api/contents?filters[contentStatus][$eq]=pending&pagination[pageSize]=1`),

                // Approved contents
                apiClient.get(`/api/contents?filters[contentStatus][$eq]=approved&pagination[pageSize]=1`)
            ]);

            const stats = {
                total: {
                    public: totalPublic.data.meta?.pagination?.total || 0,
                    private: totalPrivate.data.meta?.pagination?.total || 0,
                    draft: totalDraft.data.meta?.pagination?.total || 0,
                    pending: totalPending.data.meta?.pagination?.total || 0,
                    approved: totalApproved.data.meta?.pagination?.total || 0
                },
                totalAll: (totalPublic.data.meta?.pagination?.total || 0) +
                    (totalPrivate.data.meta?.pagination?.total || 0) +
                    (totalDraft.data.meta?.pagination?.total || 0)
            };

            // console.log('✅ Content statistics fetched:', stats);
            return stats;

        } catch (error) {
            console.error('Error fetching content statistics:', error);

            // Return empty stats instead of mock
            return {
                total: {
                    public: 0,
                    private: 0,
                    draft: 0,
                    pending: 0,
                    approved: 0
                },
                totalAll: 0
            };
        }
    }

    /**
     * ✅ NEW: Search similar content by tags
     */
    async searchSimilarContent(tags: string[], excludeId: string, limit: number = 6) {
        try {
            if (!tags || tags.length === 0) {
                return { data: [], meta: { pagination: { total: 0 } } };
            }

            // console.log(`🔍 Searching similar content by tags:`, tags);

            const queryParams = new URLSearchParams();

            // Exclude current content
            queryParams.append('filters[id][$ne]', excludeId);

            // Only public and approved content
            queryParams.append('filters[isPublic][$eq]', 'true');
            queryParams.append('filters[contentStatus][$eq]', 'approved');

            // Tags filter - search for content with any of these tags
            tags.forEach((tag, index) => {
                queryParams.append(`filters[$or][${index}][tags][name][$containsi]`, tag);
            });

            // Pagination
            queryParams.append('pagination[pageSize]', limit.toString());
            queryParams.append('pagination[page]', '1');

            // Sort by relevance (views, likes)
            queryParams.append('sort[0]', 'viewCount:desc');
            queryParams.append('sort[1]', 'likeCount:desc');

            // Populate relations
            queryParams.append('populate', JSON.stringify([
                'user',
                'category',
                'tags',
                'files',
                'thumbnail'
            ]));

            const response = await apiClient.get(`/api/contents?${queryParams.toString()}`);

            // console.log(`✅ Found ${response.data.data.length} similar contents by tags`);
            return {
                data: response.data.data || [],
                meta: response.data.meta || {}
            };

        } catch (error) {
            console.error('Error searching similar content:', error);
            return {
                data: [],
                meta: { pagination: { total: 0 } }
            };
        }
    }

    /**
     * ✅ NEW: Get pending/draft works by user - CHỈ cho owner
     */
    async getPendingContentByUser(userId: string, options: {
        limit?: number;
        page?: number;
        excludeId?: string;
    } = {}) {
        try {
            const { limit = 6, page = 1, excludeId } = options;
            // console.log(`⏳ Fetching pending content by user: ${userId}`);

            const queryParams = new URLSearchParams();

            // ✅ Filter by user ID trong owners array
            queryParams.append('filters[owners][id][$eq]', userId);

            // ✅ Filter by non-approved status
            queryParams.append('filters[$or][0][contentStatus][$eq]', 'pending');
            queryParams.append('filters[$or][1][contentStatus][$eq]', 'draft');
            queryParams.append('filters[$or][2][contentStatus][$eq]', 'rejected');

            // ✅ Exclude current work if provided
            if (excludeId) {
                queryParams.append('filters[id][$ne]', excludeId);
            }

            // ✅ Pagination
            queryParams.append('pagination[pageSize]', limit.toString());
            queryParams.append('pagination[page]', page.toString());

            // ✅ Sort by newest first
            queryParams.append('sort[0]', 'createdAt:desc');

            // ✅ Populate relations
            queryParams.append('populate', JSON.stringify([
                'category',
                'owners',
                'files',
                'tags'
            ]));

            const response = await apiClient.get(`/api/contents?${queryParams.toString()}`);

            // console.log(`✅ Found ${response.data.data?.length || 0} pending contents by user ${userId}`);
            return {
                data: response.data.data || [],
                meta: response.data.meta || {}
            };

        } catch (error) {
            console.error('❌ Error fetching pending content by user:', error);
            return {
                data: [],
                meta: { pagination: { total: 0 } }
            };
        }
    }

    /**
     * ✅ UPDATED: Cập nhật thông tin nội dung (sử dụng documentId cho Strapi v5)
     * @param id ID hoặc documentId của nội dung cần cập nhật
     * @param updateData Dữ liệu cần cập nhật
     */
    async updateContent(id: string, updateData: any) {
        try {
            const baseURL = apiClient.client?.defaults.baseURL || import.meta.env.VITE_API_URL || 'http://localhost:1337';
            const token = localStorage.getItem('token');

            // console.log(`🔄 Updating content ${id} with data:`, updateData);

            // ✅ Use universal helper với documentId support
            const response = await updateRecordById(
                baseURL,
                token,
                'content',
                id,
                updateData
            );

            return response;
        } catch (error) {
            console.error('Error updating content:', error);
            throw error;
        }
    }

    /**
     * ✅ UPDATED: Cập nhật file trong nội dung (sử dụng documentId cho Strapi v5)
     * @param id ID hoặc documentId của nội dung
     * @param formData FormData chứa file cần cập nhật
     * @param progressCallback Callback để cập nhật tiến trình upload
     */
    async updateContentFiles(id: string, formData: FormData, progressCallback?: (progress: number) => void) {
        try {
            // Lấy base URL từ apiClient
            const baseURL = apiClient.client?.defaults.baseURL || import.meta.env.VITE_API_URL || 'http://localhost:1337';
            const token = localStorage.getItem('token');

            // console.log(`📁 Updating files for content ${id}`);

            // ✅ Get documentId first for Strapi v5
            const documentId = await getDocumentId(baseURL, token, 'content', id);

            // console.log(`🔄 Using documentId ${documentId} for file update`);

            // Sử dụng axios trực tiếp để có thể theo dõi tiến trình upload
            const response = await axios({
                method: 'PUT',
                url: `${baseURL}/api/contents/${documentId}`, // Use documentId for update
                data: formData,
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': token ? `Bearer ${token}` : '',
                },
                onUploadProgress: (progressEvent) => {
                    if (progressCallback && progressEvent.total) {
                        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        progressCallback(percentCompleted);
                    }
                }
            });

            // console.log(`✅ Successfully updated files for content ${documentId}`);
            return response.data;
        } catch (error) {
            console.error('Error updating content files:', error);
            throw error;
        }
    }

    /**
     * ✅ UPDATED: Xóa nội dung (sử dụng documentId cho Strapi v5)
     * @param id ID hoặc documentId của nội dung cần xóa
     */
    async deleteContent(id: string) {
        try {
            const baseURL = apiClient.client?.defaults.baseURL || import.meta.env.VITE_API_URL || 'http://localhost:1337';
            const token = localStorage.getItem('token');

            // console.log(`🗑️ Deleting content ${id}`);

            // ✅ Use universal helper với documentId support
            const response = await deleteRecordById(
                baseURL,
                token,
                'content',
                id
            );

            // console.log(`✅ Successfully deleted content ${id}`);
            return response;
        } catch (error) {
            console.error('Error deleting content:', error);
            throw error;
        }
    }

    /**
     * ✅ THÊM: Method để debug API filters
     */
    async debugGetContents() {
        try {
            const userResponse = await apiClient.get('/api/users/me');
            const currentUserId = userResponse.data.id;

            // console.log('=== DEBUG: Current User ===');
            // console.log('User ID:', currentUserId);
            // console.log('User data:', userResponse.data);

            // Test các filter approaches khác nhau
            const approaches = [
                {
                    name: 'generatedBy filter',
                    params: {
                        'filters[generatedBy][id][$eq]': currentUserId,
                        'populate': '*'
                    }
                },
                {
                    name: 'owners filter',
                    params: {
                        'filters[owners][id][$eq]': currentUserId,
                        'populate': '*'
                    }
                },
                {
                    name: 'no filter (all contents)',
                    params: {
                        'populate': '*',
                        'pagination[pageSize]': 5
                    }
                }
            ];

            for (const approach of approaches) {
                try {
                    // console.log(`\n=== Testing ${approach.name} ===`);
                    const response = await apiClient.get('/api/contents', {
                        params: approach.params
                    });

                    // console.log(`Results: ${response.data.data.length} items`);
                    response.data.data.forEach((item, index) => {
                        const attrs = item.attributes;
                        console.log(`  ${index + 1}. ${attrs.title}`);
                        console.log(`     ID: ${item.id}, documentId: ${item.documentId}`);
                        console.log(`     generatedBy: ${attrs.generatedBy?.data?.id || 'null'}`);
                        console.log(`     owners: ${attrs.owners?.data?.map(o => o.id).join(', ') || 'none'}`);
                    });
                } catch (err) {
                    console.error(`${approach.name} failed:`, err.response?.data || err.message);
                }
            }

        } catch (error) {
            console.error('Debug failed:', error);
        }
    }

    /**
     * ✅ NEW: Bulk operations với documentId support
     */
    async bulkUpdateContents(contentIds: string[], updateData: any) {
        try {
            const baseURL = apiClient.client?.defaults.baseURL || import.meta.env.VITE_API_URL || 'http://localhost:1337';
            const token = localStorage.getItem('token');

            console.log(`🔄 Bulk updating ${contentIds.length} contents`);

            // ✅ Use strapiV5Utils bulk operations
            const result = await bulkUpdateRecords(baseURL, token, 'content', contentIds, updateData);

            const successCount = result.summary.successful;
            console.log(`✅ Bulk update completed: ${successCount}/${contentIds.length} successful`);

            return result;
        } catch (error) {
            console.error('Bulk update error:', error);
            throw error;
        }
    }

    async bulkDeleteContents(contentIds: string[]) {
        try {
            const baseURL = apiClient.client?.defaults.baseURL || import.meta.env.VITE_API_URL || 'http://localhost:1337';
            const token = localStorage.getItem('token');

            console.log(`🗑️ Bulk deleting ${contentIds.length} contents`);

            // ✅ Use strapiV5Utils bulk operations
            const result = await bulkDeleteRecords(baseURL, token, 'content', contentIds);

            const successCount = result.summary.successful;
            console.log(`✅ Bulk delete completed: ${successCount}/${contentIds.length} successful`);

            return result;
        } catch (error) {
            console.error('Bulk delete error:', error);
            throw error;
        }
    }
}

export default new ContentService();
